/**
 * Live TV: countries, then categories, then channels.
 *
 * The country pane is derived from the category list rather than fetched
 * separately, because the categories endpoint already applies the account's
 * saved country selection and the hidden flag — deriving keeps the two panes
 * from disagreeing about what exists.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { CategoryDto, StreamDto } from '@sflx/shared';
import { COMMON_COUNTRY_CODES, countryLabel, flagEmoji } from '@sflx/shared';
import { EmptyState, ErrorNotice, Loading, Spinner } from '../components/Feedback';
import { IngestingNotice } from '../components/IngestBanner';
import { FavoriteButton } from '../components/FavoriteButton';
import { Icon } from '../components/Icon';
import { SafeImage } from '../components/SafeImage';
import { useDebounced } from '../hooks/useDebounced';
import { useIngestProgress } from '../hooks/useIngestProgress';
import { useStreamFeed } from '../hooks/useStreamFeed';
import { useVirtualList } from '../hooks/useVirtualList';
import { catalog, type StreamQueryParams } from '../lib/api';
import { useAccounts } from '../lib/accounts-store';
import { formatCount } from '../lib/format';
import { watchLink } from '../lib/links';
import { setPlaybackQueue } from '../lib/queue';
import { Link, navigate } from '../router/router';

const ROW_HEIGHT = 56;
/** Sentinel for the "no country could be read" bucket; not a valid ISO code. */
const OTHER = '__other__';

type Step = 'countries' | 'categories' | 'channels';

interface CountryBucketVm {
  key: string;
  code: string | null;
  label: string;
  count: number;
}

export function Live() {
  const { active, loading: accountsLoading } = useAccounts();
  const accountId = active?.id ?? null;

  const [categories, setCategories] = useState<CategoryDto[] | null>(null);
  const [categoriesError, setCategoriesError] = useState<unknown>(null);
  const [countryKey, setCountryKey] = useState<string | null>(null);
  const [showAllCountries, setShowAllCountries] = useState(false);
  // The feed is created further down, so the settle callback reaches it through
  // a ref rather than forcing the whole thing to be declared out of order.
  const feedRef = useRef<(() => void) | null>(null);

  // While the first fetch is still running the catalog is legitimately empty.
  // Saying "No categories here." then is both wrong and the first thing a new
  // user sees, because the Live tab appears as soon as the kind is ticked.
  const ingest = useIngestProgress(accountId, () => {
    reloadCategories();
    feedRef.current?.();
  });
  const liveJob = ingest.jobs.find((job) => job.kind === 'live' && (job.status === 'queued' || job.status === 'running'));
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [step, setStep] = useState<Step>('countries');

  const debouncedSearch = useDebounced(search, 250);

  const [categoriesVersion, setCategoriesVersion] = useState(0);
  const reloadCategories = () => setCategoriesVersion((value) => value + 1);

  // Category ids are per-account panel ids, and a country code means nothing
  // outside the account it was chosen in. Switching accounts in the header
  // keeps this screen mounted, so without this the new account is queried with
  // the old one's category and country — and the pane reads "No channels here"
  // under a heading naming a category the user never picked.
  useEffect(() => {
    setCountryKey(null);
    setCategoryId(null);
    setSearch('');
    setShowAllCountries(false);
    setStep('countries');
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setCategoriesError(null);
    catalog
      // Always the whole list. The saved country selection is applied below,
      // where it can be turned off for a moment — a category the parser filed
      // under the wrong country is precisely the one that selection hides, and
      // the control that corrects it lives on the category's own row.
      .categories(accountId, 'live', undefined, undefined, 'all')
      .then((result) => {
        if (!cancelled) setCategories(result.categories);
      })
      .catch((caught: Error) => {
        if (!cancelled) setCategoriesError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, categoriesVersion]);

  // Offer the codes this account already uses first, then the common ones, so
  // correcting a mis-parsed category is a short list rather than a hunt through
  // every ISO code.
  const offeredCodes = useMemo(() => {
    const seen = new Set<string>();
    for (const category of categories ?? []) {
      if (category.countryCode) seen.add(category.countryCode);
    }
    return [...new Set([...[...seen].sort(), ...COMMON_COUNTRY_CODES])];
  }, [categories]);

  /**
   * The account's saved selection, as a predicate. `null` codes mean nothing
   * was ever chosen, which is "everything" — not "nothing".
   */
  const savedFilter = useMemo(() => {
    const codes = active?.liveCountryCodes ?? null;
    const includeOther = active?.includeOther ?? true;
    if (codes === null && includeOther) return null;
    const allowed = new Set(codes ?? []);
    return (code: string | null): boolean => (code === null ? includeOther : allowed.has(code));
  }, [active?.liveCountryCodes, active?.includeOther]);

  const scoped = useMemo(() => {
    if (!categories) return null;
    if (!savedFilter || showAllCountries) return categories;
    return categories.filter((category) => savedFilter(category.countryCode));
  }, [categories, savedFilter, showAllCountries]);

  const hiddenByFilter = (categories?.length ?? 0) - (scoped?.length ?? 0);

  // Leaving "show all" can strand the rail on a country that is no longer
  // listed, which reads as an empty screen with no way back.
  useEffect(() => {
    if (!scoped || countryKey === null) return;
    const present = scoped.some((category) => (category.countryCode ?? OTHER) === countryKey);
    if (!present) {
      setCountryKey(null);
      setCategoryId(null);
    }
  }, [scoped, countryKey]);

  // On mobile, countries/categories/channels are three separate steps, but
  // search has to work from any of them — typing a query is itself a request
  // to see channel results, so it jumps there rather than leaving the rail up.
  useEffect(() => {
    if (search.trim()) setStep('channels');
  }, [search]);

  const buckets = useMemo<CountryBucketVm[]>(() => {
    const totals = new Map<string, CountryBucketVm>();
    for (const category of scoped ?? []) {
      const key = category.countryCode ?? OTHER;
      const existing = totals.get(key);
      if (existing) existing.count += category.streamCount;
      else
        totals.set(key, {
          key,
          code: category.countryCode,
          label: category.countryCode ? countryLabel(category.countryCode) : 'Other',
          count: category.streamCount,
        });
    }
    return Array.from(totals.values()).sort((left, right) => right.count - left.count);
  }, [scoped]);

  const visibleCategories = useMemo(() => {
    const all = scoped ?? [];
    if (countryKey === null) return all;
    if (countryKey === OTHER) return all.filter((category) => category.countryCode === null);
    return all.filter((category) => category.countryCode === countryKey);
  }, [scoped, countryKey]);

  const query = useMemo<StreamQueryParams | null>(() => {
    if (!accountId) return null;
    return {
      kind: 'live',
      category: categoryId,
      q: debouncedSearch.trim() || null,
      favorites: favoritesOnly,
      // An explicitly empty code list with `other` on is how the API is asked
      // for only the unmatched bucket; omitting the key entirely falls back to
      // the account's saved selection.
      ...(countryKey === OTHER
        ? { countries: [], other: true }
        : countryKey
          ? { countries: [countryKey], other: false }
          : {}),
      limit: 120,
    };
  }, [accountId, countryKey, categoryId, debouncedSearch, favoritesOnly]);

  const feed = useStreamFeed(accountId, query);
  feedRef.current = feed.reload;

  const heading =
    categoryId && categories
      ? (categories.find((category) => category.id === categoryId)?.name ?? 'Channels')
      : countryKey === null
        ? 'All channels'
        : countryKey === OTHER
          ? 'Other'
          : countryLabel(countryKey);

  if (accountsLoading && !active) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }

  if (!active) {
    return (
      <div className="page">
        <EmptyState
          title="No account selected"
          hint="Add an Xtream account and tick Live TV to fill this screen."
          action={
            <Link className="btn btn--primary" to="/accounts">
              Go to accounts
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="page page--flush">
      {/* Lives outside the mobile step toggle below so it stays on screen
          whether countries, categories, or channels are showing. */}
      <div className="pane__search">
        <label className="row" style={{ gap: 'var(--s-3)', flexWrap: 'nowrap' }}>
          <Icon name="search" size={16} />
          <span className="sr-only">Search channels</span>
          <input
            className="input"
            type="search"
            placeholder="Search channels"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      <div className="panes" data-step={step}>
        <section className="pane" data-pane="countries" aria-label="Countries">
          <div className="pane__head">
            <span className="pane__title">Countries</span>
            {hiddenByFilter > 0 || showAllCountries ? (
              <button
                type="button"
                className="pane__action"
                onClick={() => {
                  setShowAllCountries((value) => !value);
                  setCountryKey(null);
                  setCategoryId(null);
                }}
              >
                {showAllCountries ? 'Only my countries' : `Show all (${hiddenByFilter} hidden)`}
              </button>
            ) : null}
          </div>
          <div className="pane__body">
            {categoriesError ? (
              <div style={{ padding: 'var(--s-4)' }}>
                <ErrorNotice error={categoriesError} />
              </div>
            ) : null}
            {categories === null ? (
              <div style={{ padding: 'var(--s-5)' }}>
                <Spinner label="Loading" />
              </div>
            ) : (
              <>
                <ListRow
                  selected={countryKey === null}
                  label="All channels"
                  count={buckets.reduce((sum, bucket) => sum + bucket.count, 0)}
                  onClick={() => {
                    setCountryKey(null);
                    setCategoryId(null);
                    setStep('categories');
                  }}
                />
                {buckets.map((bucket) => (
                  <ListRow
                    key={bucket.key}
                    selected={countryKey === bucket.key}
                    label={`${bucket.code ? `${flagEmoji(bucket.code)} ` : ''}${bucket.label}`}
                    count={bucket.count}
                    onClick={() => {
                      setCountryKey(bucket.key);
                      // Other has no whole-bucket query, so land on a category.
                      const firstOther =
                        bucket.key === OTHER
                          ? ((categories ?? []).find((category) => category.countryCode === null)?.id ?? null)
                          : null;
                      setCategoryId(firstOther);
                      setStep('categories');
                    }}
                  />
                ))}
              </>
            )}
          </div>
        </section>

        <section className="pane" data-pane="categories" aria-label="Categories">
          <div className="pane__head">
            <button
              type="button"
              className="btn btn--ghost btn--sm btn--icon"
              onClick={() => setStep('countries')}
              aria-label="Back to countries"
            >
              <Icon name="chevronLeft" size={15} />
            </button>
            <span className="pane__title">Categories</span>
          </div>
          <div className="pane__body">
            <ListRow
              selected={categoryId === null}
              label={
                countryKey === null
                  ? 'Everything'
                  : countryKey === OTHER
                    ? 'All Other'
                    : `All ${countryLabel(countryKey)}`
              }
              count={visibleCategories.reduce((sum, category) => sum + category.streamCount, 0)}
              onClick={() => {
                setCategoryId(null);
                setStep('channels');
              }}
            />
            {visibleCategories.map((category) => (
              <ListRow
                key={category.id}
                selected={categoryId === category.id}
                label={category.name}
                count={category.streamCount}
                onClick={() => {
                  setCategoryId(category.id);
                  setStep('channels');
                }}
                action={
                  accountId ? (
                    <CountryPin
                      accountId={accountId}
                      category={category}
                      offered={offeredCodes}
                      onChanged={reloadCategories}
                    />
                  ) : null
                }
              />
            ))}
            {categories !== null && !visibleCategories.length ? (
              liveJob ? (
                <IngestingNotice job={liveJob} />
              ) : (
                <p className="muted" style={{ padding: 'var(--s-5)' }}>
                  No categories here.
                </p>
              )
            ) : null}
          </div>
        </section>

        <ChannelPane
          accountId={active.id}
          heading={heading}
          search={search}
          favoritesOnly={favoritesOnly}
          onToggleFavorites={() => setFavoritesOnly((value) => !value)}
          onBack={() => setStep('categories')}
          feed={feed}
          ready={query !== null}
        />
      </div>
    </div>
  );
}

function ListRow({
  selected,
  label,
  count,
  onClick,
  action,
}: {
  selected: boolean;
  label: string;
  count?: number;
  onClick: () => void;
  action?: ReactNode;
}) {
  // The action cannot be nested inside the row button — a button inside a
  // button is invalid and the inner click would be swallowed — so the row is a
  // wrapper with the button and the action as siblings.
  return (
    <div className="list-row-wrap">
      <button type="button" className="list-row" aria-selected={selected} onClick={onClick}>
        <span className="list-row__label">{label}</span>
        {count === undefined ? null : <span className="list-row__count">{formatCount(count)}</span>}
      </button>
      {action}
    </div>
  );
}

/**
 * Manual country override.
 *
 * Country is read out of the category name by a heuristic, so it is sometimes
 * wrong — and when it is, the channels vanish from the user's filter with no
 * explanation. Pinning a category here always beats the parser and survives
 * the next ingest.
 */
function CountryPin({
  accountId,
  category,
  offered,
  onChanged,
}: {
  accountId: string;
  category: CategoryDto;
  offered: readonly string[];
  onChanged: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  // Three choices, because the parser has three kinds of answer to correct: a
  // wrong country, a country where there is none, and a guess worth keeping.
  const AUTO = 'auto';
  const NONE = 'none';
  const current = category.countryPinned ? (category.countryCode ?? NONE) : AUTO;

  const save = (value: string) => {
    setSaving(true);
    setFailed(false);
    catalog
      .setCategoryCountry(
        accountId,
        'live',
        category.id,
        value === AUTO || value === NONE ? null : value,
        value !== AUTO,
      )
      .then(onChanged)
      // Without this the select snaps back to its old value on the next reload
      // and the user is left thinking the app ignored them.
      .catch(() => setFailed(true))
      .finally(() => setSaving(false));
  };

  return (
    <label
      className="list-row__pin"
      title={
        failed
          ? 'That could not be saved — try again'
          : category.countryPinned
            ? 'Country set by hand'
            : "Set this category's country"
      }
    >
      <span className="sr-only">Country for {category.name}</span>
      <select
        value={current}
        disabled={saving}
        onChange={(event) => save(event.target.value)}
        data-pinned={category.countryPinned ? '' : undefined}
        data-failed={failed ? '' : undefined}
      >
        <option value={AUTO}>
          {category.countryParsed ? `Auto (${countryLabel(category.countryParsed)})` : 'Auto (Other)'}
        </option>
        <option value={NONE}>Other — no country</option>
        {offered.map((code) => (
          <option key={code} value={code}>
            {`${flagEmoji(code)} ${countryLabel(code)}`.trim()}
          </option>
        ))}
      </select>
    </label>
  );
}

interface ChannelPaneProps {
  accountId: string;
  heading: string;
  search: string;
  favoritesOnly: boolean;
  onToggleFavorites: () => void;
  onBack: () => void;
  feed: ReturnType<typeof useStreamFeed>;
  ready: boolean;
}

function ChannelPane({
  accountId,
  heading,
  search,
  favoritesOnly,
  onToggleFavorites,
  onBack,
  feed,
  ready,
}: ChannelPaneProps) {
  const { items, loading, loadingMore, error, hasMore, loadMore, total, patch } = feed;
  const list = useVirtualList({ count: items.length, rowHeight: ROW_HEIGHT });
  const { scrollToTop } = list;

  // A new list must start at the top; keeping the old scroll offset shows a
  // blank window two thousand rows into a list that now has fifty.
  const signature = `${heading}|${search}|${favoritesOnly}`;
  const previousSignature = useRef(signature);
  useEffect(() => {
    if (previousSignature.current === signature) return;
    previousSignature.current = signature;
    scrollToTop();
  }, [signature, scrollToTop]);

  // Paging is driven by the window, not by a sentinel element: with virtual
  // rows there is no DOM node at the end of the list to observe.
  useEffect(() => {
    if (hasMore && !loadingMore && list.end >= items.length - 20) loadMore();
  }, [hasMore, loadingMore, list.end, items.length, loadMore]);

  const play = (stream: StreamDto) => {
    setPlaybackQueue(
      items.map((item) => ({ accountId, kind: 'live' as const, xtreamId: item.id, name: item.name })),
    );
    navigate(watchLink({ accountId, kind: 'live', xtreamId: stream.id, title: stream.name }));
  };

  return (
    <section className="pane" data-pane="channels" aria-label="Channels">
      <div className="pane__head">
        <button
          type="button"
          className="btn btn--ghost btn--sm btn--icon"
          onClick={onBack}
          aria-label="Back to categories"
        >
          <Icon name="chevronLeft" size={15} />
        </button>
        <span className="list-row__label" style={{ fontWeight: 600 }}>
          {heading}
        </span>
        <span className="list-row__count">{total === null ? '' : formatCount(total)}</span>
        <button
          type="button"
          className="chip"
          aria-pressed={favoritesOnly}
          onClick={onToggleFavorites}
          style={{ marginLeft: 'auto' }}
        >
          <Icon name={favoritesOnly ? 'starFilled' : 'star'} size={14} />
          Favorites
        </button>
      </div>

      <div className="pane__body" ref={list.ref} onScroll={list.onScroll}>
        {error ? (
          <div style={{ padding: 'var(--s-4)' }}>
            <ErrorNotice error={error} />
          </div>
        ) : null}

        {!ready ? (
          <p className="muted" style={{ padding: 'var(--s-5)' }}>
            Pick a category to see its channels.
          </p>
        ) : loading ? (
          <div style={{ padding: 'var(--s-5)' }}>
            <Spinner label="Loading channels" />
          </div>
        ) : !items.length ? (
          <p className="muted" style={{ padding: 'var(--s-5)' }}>
            {search ? `Nothing matches "${search}".` : 'No channels here.'}
          </p>
        ) : (
          <div className="vlist" style={{ height: list.totalHeight }}>
            <div className="vlist__window" style={{ transform: `translateY(${list.offsetY}px)` }}>
              {items.slice(list.start, list.end).map((stream) => (
                <div className="channel-row" key={stream.id}>
                  <button type="button" className="channel-row__play" onClick={() => play(stream)}>
                    <span className="channel-row__logo">
                      <SafeImage src={stream.logoUrl} />
                    </span>
                    <span className="channel-row__body">
                      <span className="channel-row__name">{stream.name}</span>
                      {stream.countryCode ? (
                        <span className="channel-row__sub">
                          {flagEmoji(stream.countryCode)} {countryLabel(stream.countryCode)}
                        </span>
                      ) : null}
                    </span>
                  </button>
                  <FavoriteButton
                    accountId={accountId}
                    kind="live"
                    xtreamId={stream.id}
                    favorite={stream.favorite}
                    onChange={(favorite) => patch(stream.id, { favorite })}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {loadingMore ? (
          <div style={{ padding: 'var(--s-4)' }}>
            <Spinner label="Loading more" />
          </div>
        ) : null}
      </div>
    </section>
  );
}
